/* ============================================================
   Feature flags — LIVE-READ helpers (never cache at module load).
   The SPA does not reload after login, so a module-load constant
   would ignore a flag set from the console mid-session — which has
   bitten us before (research onboarding, Research tab). Always read
   localStorage inside the function.

   Changelog
   * 2026-09-02 TASK-194 (issue #313): the 2026-09-01 "GA on dev" flags
     (previewModeEnabled, funnelAnalyticsEnabled, nudgesEnabled,
     productTourEnabled) were default-ON EVERYWHERE, including the
     production build — nothing read VITE_ENV. Now they go through
     readFlagDevOn(): default ON on dev/ci/local, but in a PRODUCTION
     build (VITE_ENV=production, set by .github/workflows/deploy.yml +
     ci.yml) they are default OFF until a deliberate GA commit flips
     them to readFlagOn(). Prod default OFF до осознанного GA-коммита.
     readFlagOn() also returns false without a window now (it used to
     answer true there, unlike readFlag).
   * 2026-09-02 TASK-218 (issue #336): GA 2026-09-02 по решению владельца
     (#336), после TASK-195/196/214/215 — previewModeEnabled,
     funnelAnalyticsEnabled, nudgesEnabled, productTourEnabled switched from
     readFlagDevOn() to readFlagOn(): default ON in EVERY build, the
     production bundle (VITE_ENV=production) included. The owner's call:
     «отключить обязаловку в онбординге» — the preview paywall replaces the
     mandatory onboarding gate for everyone. Emergency off per browser stays
     localStorage '0'; no window → false. readFlagDevOn() is kept (and now
     exported) as the intermediate stage for the next dark feature — no flag
     uses it at the moment.
   ============================================================ */

// True only for a production bundle: the release workflows build with
// VITE_ENV=production; dev/ci/task previews and local `vite` leave it unset
// (or set something else). Guarded so non-Vite contexts fall back to "not
// production" — the safe side for a dev flag, and never the prod side.
function isProductionBuild() {
  try {
    return Boolean(import.meta.env && import.meta.env.VITE_ENV === 'production');
  } catch (_) {
    return false;
  }
}

function readFlag(key) {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(key) === '1';
  } catch (_) {
    return false;
  }
}

// Default-ON read: live for everyone unless explicitly switched off with '0'
// (the emergency kill switch). Used once a flag graduates from pilot to GA —
// in EVERY environment, production included. Without a window (tests, SSR)
// the answer is false: no browser, no feature.
function readFlagOn(key) {
  try {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(key) !== '0';
  } catch (_) {
    return true;
  }
}

// Default-ON-ON-DEV read (TASK-194, issue #313): the stage between a dark
// pilot (readFlag, '1' to enable) and GA (readFlagOn, '0' to kill).
//   * non-production build (dev/ci/local, VITE_ENV unset or anything but
//     'production') → default ON, '0' is the kill switch — same as readFlagOn.
//   * production build (VITE_ENV=production) → default OFF; only an explicit
//     localStorage '1' enables it for that browser — same as readFlag.
//   * no window → false.
// Graduating to GA = switching the flag's function to readFlagOn() in a
// deliberate commit; nothing here flips on its own. Since TASK-218 (#336)
// no flag is on this stage — exported so the next dark feature can pick it
// up and so flags.test.js keeps its semantics covered.
export function readFlagDevOn(key) {
  try {
    if (typeof window === 'undefined') return false;
    const value = window.localStorage.getItem(key);
    return isProductionBuild() ? value === '1' : value !== '0';
  } catch (_) {
    return !isProductionBuild();
  }
}

// Brand Kit + New Post composition modes — GA 2026-06-14 (default ON). The
// whole new surface (Brand Kit tab + Media nav, in-library generation, New Post
// source toggles) is live for everyone. Emergency off:
//   localStorage.setItem('dathent.brandkit.enabled', '0')
export function brandKitEnabled() {
  return readFlagOn('dathent.brandkit.enabled');
}

// Unified onboarding intake card — GA 2026-06-15 (default ON). The live
// onboarding (OnboardingChat) replaces the legacy website-fill card with the
// confirm-first "We scanned your site" card (logo + colors + business profile,
// detect + analyze in parallel). Emergency off for one browser:
//   localStorage.setItem('dathent.onboarding.intake.enabled', '0')
export function intakeCardEnabled() {
  return readFlagOn('dathent.onboarding.intake.enabled');
}

// Referral program v2 — partner application lifecycle (S0-S5) + tiers + manual
// payouts. GA 2026-06-25 (default ON for everyone). MUST ship together with the
// worker flag REFERRAL_V2='1' on BOTH auth-worker AND stripe-worker — otherwise
// the SPA shows the v2 "Apply" flow while the server is still legacy and returns
// 404/403 (see referral-apply.ts / referral-me.ts). Migrations 0073+0074 must
// already be applied. Emergency off for one browser:
//   localStorage.setItem('dathent.referral.v2', '0')
export function referralProgramV2Enabled() {
  return readFlagOn('dathent.referral.v2');
}

// Video reels (9:16 narrated slideshow) — DARK admin pilot. The server already
// double-gates (visual-worker VIDEO_REELS_ENABLED flag + the admins table), so
// this SPA flag is the FE kill switch that keeps the Reel generator hidden even
// for admins until we deliberately flip it. Default OFF. Turn on for one browser:
//   localStorage.setItem('dathent.video.reels', '1')
export function videoReelsEnabled() {
  return readFlag('dathent.video.reels');
}

// Video reels — GA'd 2026-07-09, then TURNED OFF FOR ALL 2026-07-10 (owner: pull
// all video, keep only TikTok Slides for now; re-enable once own-video upload
// exists). Back to readFlag (default OFF) so the whole Reel/Video composer is
// hidden for everyone. Server master VIDEO_REELS_ENABLED is also "0". Re-enable a
// single browser to test: localStorage.setItem('dathent.video.ga', '1').
export function videoReelsGA() {
  return readFlag('dathent.video.ga');
}

// Tier-2 CINEMATIC video — GA'd then TURNED OFF FOR ALL 2026-07-10 with the rest
// of video (see videoReelsGA). Back to readFlag (default OFF). Server
// VIDEO_CINEMATIC_ENABLED is also "0".
export function videoCinematicEnabled() {
  return readFlag('dathent.video.cinematic');
}

// Card gate (Model B) — after onboarding, a new user must put a card on file
// (7-day trial) before entering the app. GA 2026-06-25 (default ON). Server
// enforcement is the gateway's CARD_GATE_REQUIRED; this client flag decides
// whether the SPA shows the card-capture wall, and it fail-OPENs on a missing/
// exempt signal, so it can't lock out existing users (grandfathered by 0076).
// Emergency off for one browser:
//   localStorage.setItem('dathent.cardGate', '0')
export function cardGateEnabled() {
  return readFlagOn('dathent.cardGate');
}

// Stripe-hosted trial checkout — replaces the embedded Payment Element card
// gate. GA 2026-07-18 (default ON, owner decision). Emergency fall-back to the
// old embedded flow for one browser:
//   localStorage.setItem('dathent.hostedCheckout.trial', '0')
// GH-912 (Stripe Managed Payments): that override is IGNORED while new
// subscriptions are sold through Link (`managedPaymentsNewSubscriptions`).
// The embedded path (TrialCardCapture: our own SetupIntent + trial/activate)
// would create the subscription outside Managed Payments, and a card saved
// that way is not authorized for Managed Payments charges, so hosted Checkout
// is the only supported path for that cohort. The stripe-worker also refuses
// the embedded trial endpoints server-side while its flag is on.
export function hostedTrialCheckoutEnabled({ managedPaymentsNewSubscriptions = false } = {}) {
  if (managedPaymentsNewSubscriptions === true) return true;
  return readFlagOn('dathent.hostedCheckout.trial');
}

// GH-1133 S5 (design §3.4): free preview before the card. The SERVER decides
// who is in the preview (organizations.preview_arm + core_config preview/*,
// surfaced as the entitlements freePreview block); this client switch can
// only force today's card wall for one browser, never grant the preview:
//   localStorage.setItem('dathent.freePreview', '0')
export function freePreviewClientEnabled() {
  return readFlagOn('dathent.freePreview');
}

// Google Sign-In ("Continue with Google") — GA 2026-07-08 (default ON). Backend
// GOOGLE_AUTH_ENABLED='1' is live and the Google OAuth app is Published
// (non-sensitive scopes → no Google review). The server still double-gates, so
// this only decides whether the button is shown. Emergency off for one browser:
//   localStorage.setItem('dathent.googleAuth', '0')
export function googleAuthEnabled() {
  return readFlagOn('dathent.googleAuth');
}

// Multi-brand workspace ("account within an account") — GA 2026-07-20 (default
// ON, owner decision): live switcher + add-brand flow + brand scoping across
// research/strategy/content/calendar/media/socials. Backend (Phases 1+2+2b) and
// migrations 0088-0092 are live in prod; legacy rows backfilled and misaligned
// accounts consolidated. The plan entitlement (brand_profiles_total > 1) stays
// the SECOND gate: single-brand plans see the locked upsell, not the live
// switcher. Emergency off for one browser:
//   localStorage.setItem('dathent.multibrand.enabled', '0')
export function multiBrandEnabled() {
  return readFlagOn('dathent.multibrand.enabled');
}

// Email-verification wall — new open-signups must confirm their email before
// entering onboarding/app. GA 2026-06-25, then DEFAULT OFF since 2026-09-10
// (owner decision: e-mail confirmation must not block using the product —
// Settings' "Confirm your email" card, screens/Settings.jsx ProfileTab, nudges
// instead via the shared lib/useEmailVerificationActions.js hook). The
// server-side gate (gateway's EMAIL_VERIFICATION_REQUIRED) is unrelated to
// this flag and unchanged by this decision. Fail-OPEN either way (only an
// explicit emailVerified===false walls), and existing accounts are
// grandfathered by migration 0075. Opt back in for one browser/support case:
//   localStorage.setItem('dathent.emailVerify.wall', '1')
export function emailVerificationWallEnabled() {
  return readFlag('dathent.emailVerify.wall');
}

// MCP-first onboarding fork — DARK pilot, default OFF. Enable for one browser:
//   localStorage.setItem('dathent.mcponb.enabled', '1')
export function mcpOnboardingEnabled() {
  return readFlag('dathent.mcponb.enabled');
}

// Own-video publishing v2 + TikTok compliance — GA 2026-07-30 (default ON):
// upload your own video in Media and publish it to TikTok (inbox draft),
// Instagram Reels, Facebook and Threads. Emergency off for one browser:
//   localStorage.setItem('dathent.video.publish.v2', '0')
export function videoPublishV2Enabled() {
  return readFlagOn('dathent.video.publish.v2');
}

// TikTok Direct Post — GA default ON since 2026-08-09: TikTok approved the
// Content Posting audit for the Dathent.AI app on 2026-08-07, so the compliance
// card offers "Post directly to TikTok" (and defaults to it) alongside inbox
// drafts. Emergency off for one browser:
//   localStorage.setItem('dathent.tiktok.direct', '0')
export function tiktokDirectPostEnabled() {
  return readFlagOn('dathent.tiktok.direct');
}

// Brand typography — DARK phase 1 pilot (issue #61), default OFF. Enable for
// one browser: localStorage.setItem('dathent.textstyle', '1').
export function brandTypographyEnabled() {
  return readFlag('dathent.textstyle');
}

// Preview paywall (TASK-143, issue #236) — DARK pilot, default OFF. Owner's
// conversion redesign: a new user skips mandatory onboarding, lands in the
// live product, and after a short active-preview window the app blurs behind
// a subscribe CTA. The SPA layer is presentation only — the gateway's
// CARD_GATE_REQUIRED keeps denying mutations (402) for non-billing users
// regardless of this flag. Enable for one browser:
//   localStorage.setItem('dathent.preview.mode', '1')
// GA on dev 2026-09-01 (owner: «убрать блокирующий онбординг») — default ON
// on dev/ci, emergency off for one browser:
//   localStorage.setItem('dathent.preview.mode','0')
// TASK-194 (issue #313) kept production default OFF (readFlagDevOn) until a
// deliberate GA commit. TASK-218 (issue #336) IS that commit — GA 2026-09-02
// by owner decision, after TASK-195/196/214/215: default ON in every build,
// production included. Server enforcement (gateway CARD_GATE_REQUIRED) is
// unchanged; this only decides whether the SPA shows the preview flow
// instead of the mandatory-onboarding gate.
export function previewModeEnabled() {
  return readFlagOn('dathent.preview.mode');
}

// Active-preview budget in seconds before the blur. Config, not constant, so
// EPIC-019 can A/B the threshold. Counts ACTIVE time (tab visible + recent
// input), not wall-clock.
export function previewSeconds() {
  try {
    const raw = window.localStorage.getItem('dathent.preview.seconds');
    const n = parseInt(raw ?? '', 10);
    return Number.isFinite(n) && n >= 10 ? n : 120;
  } catch (_) {
    return 120;
  }
}

// Funnel-event tracker (TASK-122, issue #214) — DARK pilot, default OFF.
// Gates lib/funnel.js's track()/trackOnce() entirely: while off, the tracker
// is a complete no-op (no console.debug, no send, no localStorage queue
// write) so it can ship with zero footprint until EPIC-019/018 are ready to
// read the data. Enable for one browser:
//   localStorage.setItem('dathent.funnel.analytics', '1')
export function funnelAnalyticsEnabled() {
  // GA on dev 2026-09-01 with the onboarding-redesign flags — the funnel must
  // capture their events. Emergency off: localStorage.setItem('dathent.funnel.analytics','0')
  // TASK-194 (issue #313) kept production default OFF; TASK-218 (issue #336)
  // GA 2026-09-02 by owner decision — default ON in every build, production
  // included, so the funnel sees the preview/nudge/tour events there too.
  return readFlagOn('dathent.funnel.analytics');
}

// Onboarding nudge bell (TASK-144, issue #237) — DARK pilot, default OFF.
// Owner's "recommend, don't force" onboarding redesign: since the preview
// paywall (TASK-143) already lets users skip onboarding into the live
// product, this is the frequent-but-not-annoying reminder banner (finish
// onboarding / finish in ChatGPT / connection guide) that nudges them back
// instead of a hard gate. Gates OnboardingNudge entirely — while off the
// component renders nothing and does no extra getBrandProfile polling
// beyond what RequireOnboarding already does. Enable for one browser:
//   localStorage.setItem('dathent.nudges.enabled', '1')
export function nudgesEnabled() {
  // GA on dev 2026-09-01 (owner). Emergency off: localStorage.setItem('dathent.nudges.enabled','0')
  // TASK-194 (issue #313) kept production default OFF; TASK-218 (issue #336)
  // GA 2026-09-02 by owner decision — default ON in every build, production
  // included.
  return readFlagOn('dathent.nudges.enabled');
}

// Generation Level picker (Economy/Standard/Premium — TASK-236/241, EPIC-023
// pricing v2, components/GenerationLevelPicker.jsx). HIDDEN 2026-09-08 (owner
// decision, after the picker shipped to prod): credit spending per level is
// still fully dark, and the picker's OWN UI default (DEFAULT_GENERATION_LEVEL
// = 'economy' in lib/generationLevel.js) was silently routing every new post
// through the cheap nano model instead of the pre-picker gpt-4.1 default —
// see ai-router provider-routing.ts textLevelModels(). Hiding the picker in
// CreatePost/EditPost AND omitting `level`/`metadata.level` from every
// generation request restores byte-identical pre-picker behavior: an absent
// level resolves to content-worker's/ai-router's own default ("standard"),
// which is wired to be identical to the old hardcoded AI_TEXT_DRAFT_* config.
// Default OFF. Enable for one browser to test:
//   localStorage.setItem('dathent.generationLevel', '1')
export function generationLevelPickerEnabled() {
  return readFlag('dathent.generationLevel');
}

// Product tour (TASK-145, issue #238) — DARK pilot, default OFF. Owner's
// "обучашка": a thin, no-SDK, self-built product tour (ProductTour.jsx) that
// walks a first-time /app visitor through a handful of contextual tooltips
// (dashboard, new post, calendar, connect social, Brand Kit, notification
// bell) in the user's own i18n language, with a "Skip tour" button on every
// step (owner decision 2026-08-31, issue #238). Per-user completion is
// tracked in localStorage ('dathent.tour.done.<userId>'), independent of
// this flag, so flipping the flag off mid-rollout doesn't wipe who already
// finished/skipped. Enable for one browser:
//   localStorage.setItem('dathent.tour.enabled', '1')
export function productTourEnabled() {
  // GA on dev 2026-09-01 (owner). Emergency off: localStorage.setItem('dathent.tour.enabled','0')
  // TASK-194 (issue #313) kept production default OFF; TASK-218 (issue #336)
  // GA 2026-09-02 by owner decision — default ON in every build, production
  // included.
  return readFlagOn('dathent.tour.enabled');
}
